/**
 * Utilidades para los reducers de Kanban
 * Este archivo contiene funciones de utilidad comunes para los reducers de Kanban
 */

import moment from 'moment';
import 'moment-timezone';

// Configurar moment en español para Kanban
moment.locale('es-mx');

/**
 * Compara dos arrays de objetos para determinar si son iguales
 * @param {Array} arr1 - Primer array
 * @param {Array} arr2 - Segundo array
 * @param {string} idField - Campo a usar como identificador único
 * @returns {boolean} - true si los arrays son iguales, false en caso contrario
 */
export const areArraysEqual = (arr1, arr2, idField = 'id') => {
  if (!arr1 || !arr2) {
    return false;
  }

  if (arr1.length !== arr2.length) {
    return false;
  }

  // Crear mapas para comparación rápida
  const map1 = new Map(arr1.map(item => [String(item[idField]), item]));
  const map2 = new Map(arr2.map(item => [String(item[idField]), item]));

  // Verificar que todos los IDs de arr1 estén en arr2
  for (const key of map1.keys()) {
    if (!map2.has(key)) {
      return false;
    }

    // Comparar también los valores importantes
    const item1 = map1.get(key);
    const item2 = map2.get(key);

    // Comparar propiedades importantes
    if (item1.name !== item2.name ||
        item1.position !== item2.position ||
        item1.board_id !== item2.board_id) {
      return false;
    }
  }

  // Verificar que todos los IDs de arr2 estén en arr1
  for (const key of map2.keys()) {
    if (!map1.has(key)) {
      return false;
    }
  }
  return true;
};

/**
 * Formatea un ticket para mantener una estructura consistente
 * @param {Object} ticket - Ticket a formatear
 * @param {Object} additionalData - Datos adicionales para incluir
 * @returns {Object} - Ticket formateado
 */
export const formatTicket = (ticket, additionalData = {}) => {
  const createdAt = ticket.created_at || additionalData.created_at;
  const updatedAt = ticket.updated_at || additionalData.updated_at;
  
  // Formatear fechas
  const { formattedDate: formattedCreatedAt, isToday: isCreatedToday, fullDate: fullCreatedAt } = formatTicketDate(createdAt);
  const { formattedDate: formattedUpdatedAt, isToday: isUpdatedToday, fullDate: fullUpdatedAt } = formatTicketDate(updatedAt);
  
  return {
    id: ticket.id || ticket._id,
    name: ticket.name || '',
    description: ticket.description || additionalData.description || '',
    meta_data: ticket.meta_data || additionalData.meta_data || '{}',
    created_by_agent: String(ticket.created_by_agent || ''),
    id_customer: ticket.id_customer,
    id_contact: ticket.id_contact,
    id_agent: ticket.id_agent || additionalData.id_agent || null,
    assigned_to_agent: ticket.assigned_to_agent ? String(ticket.assigned_to_agent) : null,
    column_id: ticket.column_id || additionalData.column_id,
    created_at: createdAt,
    updated_at: updatedAt,
    status: ticket.status || 'pending',
    contact_name: ticket.contact_name || ticket.name_contact || additionalData.name_contact || null,
    assigned_name: ticket.assigned_name || ticket.assigned_agent_name || additionalData.assigned_agent_name || null,
    creator_agent_name: ticket.creator_agent_name || additionalData.creator_agent_name || null,
    name_contact: ticket.name_contact || additionalData.name_contact || null,
    assigned_agent_name: ticket.assigned_agent_name || additionalData.assigned_agent_name || null,
    
    // Campos formateados para la vista
    formatted_created_at: formattedCreatedAt,
    is_created_today: isCreatedToday,
    full_created_at: fullCreatedAt,
    
    formatted_updated_at: formattedUpdatedAt,
    is_updated_today: isUpdatedToday,
    full_updated_at: fullUpdatedAt
  };
};



/**
 * Formatea la fecha de un ticket para mostrarla de manera amigable en zona horaria de México
 * @param {string|Date} date - Fecha a formatear (se asume UTC desde la base de datos)
 * @returns {Object} - Objeto con la fecha formateada y un indicador de si es de hoy
 */
export const formatTicketDate = (date) => {
  if (!date) return { formattedDate: '', isToday: false };
  
  // Convertir la fecha UTC directamente a zona horaria de México
  const momentDate = moment.utc(date).tz('America/Mexico_City');
  // Usar el inicio del día en la zona horaria de México para comparación
  const todayStart = moment().tz('America/Mexico_City').startOf('day');
  const isToday = momentDate.isSameOrAfter(todayStart, 'second');
  
  // Formatear la fecha según si es hoy o no
  const formattedDate = isToday 
    ? momentDate.format('LT')  // Solo hora si es hoy
    : momentDate.format('lll'); // Fecha y hora completa si no es hoy
  
  return {
    formattedDate,
    isToday,
    fullDate: momentDate.format('lll'), // Fecha completa formateada
    timestamp: momentDate.valueOf() // Timestamp para ordenamiento
  };
};

/**
 * Sistema de logging estructurado que puede ser desactivado en producción
 */
export const logger = {
  debug: (message, ...args) => {
    if (process.env.NODE_ENV !== 'production') {

    }
  },
  info: (message, ...args) => {
    if (process.env.NODE_ENV !== 'production') {
      console.info(`[INFO] ${message}`, ...args);
    }
  },
  warn: (message, ...args) => {
    console.warn(`[WARN] ${message}`, ...args);
  },
  error: (message, ...args) => {
    console.error(`[ERROR] ${message}`, ...args);
  }
};
